import mermaid from "mermaid";
import { Checkbox, theme } from "antd";
import { useCallback, useEffect, useRef, useState } from "react";
import { queryExplain } from "@vzbl/integrations";
import { useIntegrationConfig } from "src/search/lib/useIntegrationConfig";
import { SearchState } from "src/search/types";
import { usePopupContext } from "src/hooks/usePopup";
import { Flex } from "src/components/Flex";

const Mermaid = ({
  chart,
  onRender,
}: {
  chart: string;
  onRender?: () => void;
}) => {
  const onRenderCallback = useRef(onRender);
  onRenderCallback.current = onRender;
  const [data, setData] = useState<string>("rendering...");
  useEffect(() => {
    onRenderCallback.current && onRenderCallback.current();
  }, [data]);

  useEffect(() => {
    // Configure mermaid if necessary
    mermaid.initialize({
      startOnLoad: true,
      theme: "default",
      // Add any other mermaid configuration options here
    });
    async function renderMermaid() {
      const { svg } = await mermaid.render("chartDiv", chart);
      setData(svg);
    }
    renderMermaid();

    // Render the chart
  }, [chart]);

  return (
    <div dangerouslySetInnerHTML={{ __html: data }} style={{ width: 1200 }} />
  );
};

export const QueryExplain = ({
  state,
  autoResizePopup,
}: {
  state: SearchState;
  autoResizePopup?: boolean;
}) => {
  const { token } = theme.useToken();
  const config = useIntegrationConfig();
  const [showRels, setShowRels] = useState<boolean>(false);
  const [showActs, setShowActs] = useState<boolean>(false);

  const explainData = queryExplain(config.data as any, state as any);
  const chart = explainToMermaid(explainData, showRels, showActs);

  const popup = usePopupContext();
  const handleOnRender = useCallback(() => {
    if (autoResizePopup) popup.resize();
  }, [autoResizePopup, popup]);
  return (
    <Flex
      direction="column"
      style={{ maxHeight: "100%", position: "relative" }}
    >
      <div
        style={{
          position: "sticky",
          top: 4,
          zIndex: 1,
        }}
      >
        <Flex gap={2}>
          <div
            style={{
              backgroundColor: token.colorBgBase,
              padding: 4,
              paddingLeft: 16,
              borderRadius: 8,
              borderTopRightRadius: 0,
              borderBottomRightRadius: 0,
            }}
          >
            <Checkbox
              checked={showRels}
              onChange={(e) => setShowRels(e.target.checked)}
            >
              show whole relationship schema
            </Checkbox>
          </div>
          <div
            style={{
              backgroundColor: token.colorBgBase,
              padding: 4,
              paddingLeft: 16,
              borderRadius: 8,
              borderTopLeftRadius: 0,
              borderBottomLeftRadius: 0,
            }}
          >
            <Checkbox
              checked={showActs}
              onChange={(e) => setShowActs(e.target.checked)}
            >
              show whole action schema
            </Checkbox>
          </div>
        </Flex>
      </div>
      <div style={{ margin: 16, maxWidth: "100%", overflow: "auto" }}>
        <Mermaid chart={chart} onRender={handleOnRender} />
      </div>
    </Flex>
  );
};

const explainToMermaid = (
  explainData: any,
  showSetSchema: boolean,
  showActSchema: boolean
): string => {
  const relToMer = (rel: {
    from: string;
    to: string;
    name: string;
    dir?: "direct" | "back";
  }) => {
    const { from, to, name, dir } = rel;
    const [start, end] = dir == "back" ? [to, from] : [from, to];
    return `id_${start}(${start})-->|${name}|id_${end}(${end})`;
  };

  const activeRels: string[] = [
    ...new Set(explainData.relationships.map(relToMer)),
  ] as string[];
  const schemaSetRels: string[] =
    explainData.setSchema.relationships.map(relToMer);
  const schemaActRels: string[] =
    explainData.actSchema.relationships.map(relToMer);

  const actColor = "lightCoral";
  const setColor = "SkyBlue";

  const activeLinkStyles = activeRels.map((r: string, idx: number) => {
    const isAct = schemaActRels.includes(r);
    const color = isAct ? actColor : setColor;
    return `linkStyle ${idx} color:${color},stroke:${color}, stroke-width:6px`;
  });

  let linkOffset = activeRels.length;

  const displaySchemaSetRels = schemaSetRels.filter(
    (r: string) => !activeRels.includes(r)
  );
  const setLinkStyle =
    "linkStyle " +
    new Array(displaySchemaSetRels.length)
      .fill(0)
      .map((_, idx) => `${idx + linkOffset}`)
      .join(",") +
    " stroke-width:1px, stroke:SkyBlue, color:SkyBlue";

  linkOffset =
    activeRels.length + (showSetSchema ? displaySchemaSetRels.length : 0);
  const displaySchemaActRels = schemaActRels.filter(
    (r: string) => !activeRels.includes(r)
  );
  const actLinkStyle =
    "linkStyle " +
    new Array(displaySchemaActRels.length)
      .fill(0)
      .map((_, idx) => `${idx + linkOffset}`)
      .join(",") +
    " stroke-width:1px, stroke:lightCoral, color:lightCoral";

  return `
    flowchart LR
    ${activeRels.join(";\n")};
    ${showSetSchema ? displaySchemaSetRels.join(";\n") : ""};
    ${showActSchema ? displaySchemaActRels.join(";\n") : ""};

    ${activeLinkStyles.join(";\n")};
    ${showSetSchema ? setLinkStyle : ""};
    ${showActSchema ? actLinkStyle : ""};
    `;
};
